import "./CampaignMappingPendingChanges.scss";
import React from "react";
import { MdClose } from "react-icons/md";
import {
  buildOptionIndexByLevel,
  estimateFanOut,
  getEffectiveSelection,
  getLevelLabel,
  makeMappingOptionKey,
  MAPPED_ID_FIELD_BY_LEVEL,
  MAPPING_LEVELS,
} from "./CampaignMappingUtils";
import {
  CampaignEdit,
  CampaignEditsMap,
  CampaignMappingRow,
  MappingLevel,
  MappingOption,
} from "@blisspointmedia/bpm-types/dist/CampaignMappingTool";

interface CampaignMappingPendingChangesProps {
  allRows: readonly CampaignMappingRow[];
  editsMap: CampaignEditsMap;
  dataByRawDataId: Record<string, CampaignMappingRow>;
  mappingOptions: Record<MappingLevel, MappingOption[]>;
  onRemoveEdit: (rawDataId: string) => void;
  onClose: () => void;
}

interface LevelChange {
  level: MappingLevel;
  from: string;
  to: string;
  cleared: boolean;
}

const EMPTY_DISPLAY = "—";

const collectLevelChanges = (
  row: CampaignMappingRow,
  edit: CampaignEdit,
  optionByKeyByLevel: Record<MappingLevel, Map<string, MappingOption>>
): LevelChange[] => {
  const changes: LevelChange[] = [];
  for (const level of MAPPING_LEVELS) {
    if (!(level in edit)) {
      continue;
    }
    const slot = edit[level];
    const existingMappedId = row[MAPPED_ID_FIELD_BY_LEVEL[level]] || "";
    const newId = slot?.id || "";
    if (
      newId === existingMappedId &&
      (!slot ||
        (slot.accountId === (row.mapped_account_id || "") &&
          slot.source === (row.mapped_campaign_source || "")))
    ) {
      continue;
    }
    const existingOption = existingMappedId
      ? optionByKeyByLevel[level].get(
          makeMappingOptionKey(
            existingMappedId,
            row.mapped_account_id || "",
            row.mapped_campaign_source || ""
          )
        )
      : null;
    changes.push({
      level,
      from: existingOption?.label || existingMappedId || EMPTY_DISPLAY,
      to: slot ? slot.label : EMPTY_DISPLAY,
      cleared: slot === null,
    });
  }
  return changes;
};

const CampaignMappingPendingChanges: React.FC<CampaignMappingPendingChangesProps> = ({
  allRows,
  editsMap,
  dataByRawDataId,
  mappingOptions,
  onRemoveEdit,
  onClose,
}) => {
  const optionByKeyByLevel = React.useMemo(() => buildOptionIndexByLevel(mappingOptions), [
    mappingOptions,
  ]);

  const editEntries = Object.values(editsMap);

  return (
    <div className="campaignPendingChangesContainer">
      <div className="campaignPendingChangesHeader">
        Pending Changes
        <button className="closeButton" onClick={onClose}>
          <MdClose />
        </button>
      </div>
      <div className="campaignPendingChangesBody">
        {editEntries.length === 0 ? (
          <div className="emptyState">No pending changes.</div>
        ) : (
          editEntries.map(edit => {
            const row = dataByRawDataId[edit.rawDataId];
            const changes = row ? collectLevelChanges(row, edit, optionByKeyByLevel) : [];
            const fanOut = row ? estimateFanOut(row, edit, allRows) : 0;
            const echoed = row
              ? MAPPING_LEVELS.filter(
                  level => !(level in edit) && row[MAPPED_ID_FIELD_BY_LEVEL[level]]
                ).map(level => ({
                  level,
                  label: getEffectiveSelection(row, edit, level, optionByKeyByLevel)?.label || "",
                }))
              : [];

            return (
              <div key={edit.rawDataId} className="pendingEditItem">
                <div className="editItemHeader">
                  <div className="rowIdentifier">
                    <span className="analyticsCampaign">
                      {row?.analytics_campaign_name || row?.analytics_campaign_id || edit.rawDataId}
                    </span>
                    {row?.analytics_source && (
                      <span className="sourceTag">{row.analytics_source}</span>
                    )}
                  </div>
                  <button className="removeEdit" onClick={() => onRemoveEdit(edit.rawDataId)}>
                    <MdClose />
                  </button>
                </div>
                {changes.map(change => (
                  <div key={change.level} className="mappingDetail">
                    <span className="mappingLabel">{getLevelLabel(change.level)}:</span>
                    <span className="mappingValue">
                      {change.from} → {change.cleared ? <em>Cleared</em> : change.to}
                    </span>
                  </div>
                ))}
                {echoed.length > 0 && (
                  <div className="echoedMappings">
                    Keeps: {echoed.map(e => `${getLevelLabel(e.level)} ${e.label}`).join(", ")}
                  </div>
                )}
                {fanOut > 1 && <div className="fanOutEstimate">Affects {fanOut} rows</div>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CampaignMappingPendingChanges;
